import type { AttackType, DamageType, MovementType } from '../characters/types';
import type { BattleResult, PieceId } from '../game/types';

/** How a tile treats anything trying to occupy it. */
export type TileWalk = 'ground' | 'liquid' | 'blocked';

/**
 * One tile family. Almost every arena feature is a field here rather than engine code, which is
 * what "content is data" is meant to buy: hazards, ice, roads and lamps are all tilesets.
 */
export interface TileDef {
  id: string;
  walk: TileWalk;
  /** Metres of the tile's own obstacle; 0 for anything flat. */
  height: number;
  /** Multiplies a fighter's speed while it stands here. */
  speedScale?: number;
  /** Ground friction, 0…1; low values slide. */
  friction?: number;
  /** Hazard damage per second while standing here (`hazard.ts`: fliers pass over). */
  damagePerSecond?: number;
  /** The hazard's kind of harm: a fighter's resistance to it slows the burn (lava is `fire`). */
  damageType?: DamageType;
  /** Colour and range of a lamp on this tile (rendering). */
  light?: { color: string; range: number };
  /** Variant-picking scheme for the renderer. */
  autotile?: string;
  /** Named shader for the special cases (water). */
  material?: string;
  /** How the arena editor shows the tile: nothing a duel reads (editor:quality#16). */
  editor?: TileEditorLook;
}

/** A tile in the arena editor: the colour it is painted in, and the name its brush carries. */
export interface TileEditorLook {
  /** `#rrggbb`. */
  color?: string;
  label?: string;
}

/** A shape of tiles the editor paints in one stroke: rows of tile ids. */
export interface TileStamp {
  name: string;
  cells: string[][];
}

/** A named set of tile families that arenas draw their legends from. */
export interface TilesetDef {
  id: string;
  name?: string;
  tiles: TileDef[];
  /** The editor's stamps for this tileset's tiles; editor data, as `TileDef.editor` is. */
  stamps?: TileStamp[];
}

/** How an arena is framed by the fixed camera. */
export interface ArenaCamera {
  /** `fit` frames the whole arena; `scroll` tracks `region` tiles over a larger map. */
  mode: 'fit' | 'scroll';
  /** Tiles framed at once; required for `scroll`, ignored otherwise. */
  region?: [number, number];
}

/** A fog centre: visual only for now, but in the arena data so it can become tactical. */
export interface ArenaFog {
  /** Tile the fog is centred on. */
  at: [number, number];
  /** Tiles from the centre; the outer 30 % fades out. */
  radius: number;
}

/**
 * Arena definition (what `content/arenas/*.json` and the arena editor store): one tile per cell
 * in row strings, exactly like `BoardDef`. `x` grows east, `y` grows south, `y = 0` is the first
 * row. World space puts the tile grid on the ground plane, so tile `y` is world `z`.
 */
export interface ArenaDef {
  id: string;
  name: string;
  width: number;
  height: number;
  /** Tileset the legend's families are looked up in. */
  tileset: string;
  /** One character per cell mapped to a tile family id. */
  legend: Record<string, string>;
  /** One string per row, `height` rows of `width` characters. */
  rows: string[];
  /** Editor group footprints, persisted independently of hover/selection. */
  groups?: { x: number; y: number; width: number; height: number }[];
  camera?: ArenaCamera;
  /** Spawn points in tile coordinates, in seat order, up to six. */
  spawns: [number, number][];
  /** Seconds after which the fittest fighter wins. */
  timeout?: number;
  /** Seconds after which the safe ring starts shrinking. */
  suddenDeathAt?: number;
  fog?: ArenaFog[];
  theme?: { sky?: string; ambience?: string };
}

export interface Vec3 {
  x: number;
  y: number;
  z: number;
}

/**
 * An opaque seat id. v1 fights two seats, `attacker` and `defender`, but nothing in the
 * simulation depends on there being exactly those two: arenas carry up to six ordered spawns
 * (D-19) and the state keys its fighters by whatever ids the seats were given.
 *
 * Because this is a string, `state.fighters[id]` is `Fighter | undefined` under
 * `noUncheckedIndexedAccess`: reach for `fighterOf(state, id)` instead.
 */
export type FighterId = string;

export const ATTACKER: FighterId = 'attacker';
export const DEFENDER: FighterId = 'defender';

/** What a player (or the AI) wants this tick. Movement is in the fighter's local frame. */
export interface FighterInput {
  /** Authored by the Arena AI controller only; slows locomotion against an actual human. */
  humanFriendlyAi?: boolean;
  /** −1 … 1: strafe left/right. */
  moveX: number;
  /** −1 … 1: backwards/forwards. */
  moveZ: number;
  /** AI-only multiplier on the fighter's own speed; human inputs remain at one. */
  speedScale?: number;
  /** Radians around the vertical axis; 0 faces +z. */
  yaw: number;
  /** Radians up/down, clamped to ±80°. */
  pitch: number;
  /** A burst of speed along the way of travel; replaces the jump the voxel arena had. */
  dash: boolean;
  attack: boolean;
  /** −1 … 1: bends a steerable magic projectile left/right while it is in flight. */
  steer?: number;
}

export const NO_INPUT: FighterInput = {
  moveX: 0,
  moveZ: 0,
  yaw: 0,
  pitch: 0,
  dash: false,
  attack: false,
  steer: 0,
};

export interface Fighter {
  id: FighterId;
  pieceId: PieceId;
  faction: string;
  character: string;
  /** Feet position. `y` is the standing height: 0 on the ground, `FLIGHT_HEIGHT` for a flier. */
  pos: Vec3;
  vel: Vec3;
  yaw: number;
  pitch: number;
  hp: number;
  /**
   * The character this fighter fights as when it is not its own: a shapeshifter's copy of its
   * opponent (D-56). Level stats and the duel AI read this one.
   */
  form?: string;
  /** Board wounds and unscaled starting health, retained across AI difficulty scaling. */
  boardDamage?: number;
  boardStartingHp?: number;
  maxHp: number;
  /** Which tiles this fighter may cross (`packages/core/src/characters/types.ts`). */
  movement: MovementType;
  /**
   * What this fighter is worth, for the timeout verdict. Resolved once at the start of the duel
   * from the roster's `force`; a character without one is worth what its opponents are worth, and
   * a duel where nobody has a force gives everyone 1. The verdict then comes down to the share of
   * hit points left, which is what a player can read off the health bars.
   */
  force: number;
  attackType: AttackType;
  damageType: DamageType;
  effectChance: number;
  protection: number;
  resistances: Partial<Record<DamageType, number>>;
  damage: number;
  /** Metres for melee/aura; ignored for projectiles. */
  range: number;
  /** Seconds between attacks. */
  attackInterval: number;
  /** True only for a spellcaster's projectile weapon (the original Wizard and Sorceress). */
  steerableProjectile?: boolean;
  cooldown: number;
  /**
   * Seconds left before a wound-up attack lands (D-42): a blow, an aura or a burst is pressed, then
   * lands this long after, where the fighter was facing when it pressed. Zero, or absent in a fight
   * saved before the wind-up, when none is coming.
   */
  windup?: number;
  /** Seconds of hit stun left. */
  stun: number;
  alive: boolean;
  /** Collision half-width and height in metres. */
  halfWidth: number;
  height: number;
  /** Forward run speed, m/s. */
  speed: number;
  /**
   * Share of full pace while its piece is under Curse (`CURSE_PACE`); absent for everyone else.
   * `speed` and `attackInterval` already carry it; the dash, which does not follow `speed`, reads
   * it here, and so does the view that says the fighter is cursed.
   */
  pace?: number;
  /** Seconds of dash left, and seconds until another may be started. */
  dashTime: number;
  dashCooldown: number;
  /** Direction of the dash in progress. */
  dashDir: { x: number; z: number } | null;
  /**
   * The burning ring's account, in seconds of fire left, for an `attackType` of `burn`; zero and
   * unused for everyone else. It empties while the ring is up and fills again while it is down,
   * both at one second per second, so half an account spent is half a recharge away from full and
   * a quarter-spent account still buys three quarters of the time.
   */
  burn: number;
  /** Full size of the ring account; defaults to three seconds for older serialized fights. */
  burnCapacity?: number;
  /** Whether the ring is up this step: what the view draws, and what the account is paying for. */
  burning: boolean;
  /** Seconds accumulated towards the ring's next bite, so it burns at a fixed rate. */
  burnTick: number;
}

export interface Projectile {
  id: number;
  owner: FighterId;
  pos: Vec3;
  vel: Vec3;
  damage: number;
  /** Seconds left. */
  ttl: number;
  radius: number;
  /** Total horizontal bend from the launch direction, radians. */
  turn?: number;
  /** Whether its owner may bend it with live input. */
  steerable?: boolean;
}

/**
 * What took the health (D-34). Only a `blow` staggers: a weapon's hit on its target. A burning
 * ring's `ring` bites, the ground's `hazard` (a tile's damage, or the closing ring of sudden
 * death) and a burst's `cost` to its own owner take health and nothing else.
 */
export type HitSource = 'blow' | 'ring' | 'hazard' | 'cost';

export type BattleEvent =
  /** A wound-up attack was pressed: it lands `WIND_UP` seconds later, as its `attack`. */
  | { type: 'windup'; fighter: FighterId; kind: AttackType }
  | { type: 'attack'; fighter: FighterId; kind: AttackType }
  | { type: 'hit'; target: FighterId; damage: number; by: FighterId; source: HitSource }
  | { type: 'projectile-hit-world'; at: Vec3; owner: FighterId }
  | { type: 'death'; fighter: FighterId }
  /** A fighter blew itself up: a forfeit, and a blast the view should show. */
  | { type: 'suicide'; fighter: FighterId; at: Vec3 }
  | { type: 'sudden-death' }
  /** The clock ran out; `winner` is the seat the fitness verdict gave the square to. */
  | { type: 'timeout'; winner: FighterId | null };

export interface BattleState {
  arenaId: string;
  /** Seconds since the fight started. */
  time: number;
  /** Seat ids in spawn order; `fighters` is keyed by these. */
  seats: FighterId[];
  /** The seat holding the contested square: it keeps it when the timeout verdict ties. */
  defenderSeat: FighterId;
  fighters: Record<FighterId, Fighter>;
  projectiles: Projectile[];
  nextProjectileId: number;
  /**
   * The shrinking safe zone. It starts covering the whole arena and closes in from
   * `suddenDeathAt`, around the point the fighters had reached by then rather than the middle of
   * the map — on a large `scroll` arena the middle can be nowhere near the fight.
   */
  safeRadius: number;
  safeCentre: { x: number; z: number };
  /** Whether the ring has started closing in; the view and the AI read this, not a clock. */
  suddenDeath: boolean;
  rng: number;
  result: BattleResult | null;
  /** Events raised during the last step (rendering, sound); cleared every step. */
  events: BattleEvent[];
  /** Internal fixed-step transaction; absent outside stepBattle. */
  pendingHits?: { target: FighterId; damage: number; by: FighterId; source: HitSource }[];
}

/** The character a fighter fights as: its own, or the one a shapeshifter copied (D-56). */
export function fightsAs(f: Fighter): string {
  return f.form ?? f.character;
}

/** The fighter in a seat. Throws for an id the battle does not have, which is always a bug. */
export function fighterOf(state: BattleState, id: FighterId): Fighter {
  const f = state.fighters[id];
  if (!f) throw new Error(`No fighter '${id}' in this battle`);
  return f;
}

/** Every fighter, in seat order. */
export function fightersOf(state: BattleState): Fighter[] {
  return state.seats.map((id) => fighterOf(state, id));
}
